export type ReleaseUpdateCategory =
  | 'NEW_FEATURE'
  | 'IMPROVEMENT'
  | 'BUG_FIX'
  | 'SECURITY_UPDATE'
  | 'BREAKING_CHANGE';

export const RELEASE_UPDATE_CATEGORIES: ReleaseUpdateCategory[] = [
  'NEW_FEATURE',
  'IMPROVEMENT',
  'BUG_FIX',
  'SECURITY_UPDATE',
  'BREAKING_CHANGE',
];

export const RELEASE_UPDATE_CATEGORY_LABELS: Record<ReleaseUpdateCategory, string> = {
  NEW_FEATURE: 'New Feature',
  IMPROVEMENT: 'Improvement',
  BUG_FIX: 'Bug Fix',
  SECURITY_UPDATE: 'Security Update',
  BREAKING_CHANGE: 'Breaking Change',
};

export const RELEASE_UPDATE_CATEGORY_SECTIONS: {
  category: ReleaseUpdateCategory;
  label: string;
  emoji: string;
}[] = [
  { category: 'NEW_FEATURE', label: 'New Features', emoji: '🚀' },
  { category: 'IMPROVEMENT', label: 'Improvements', emoji: '✨' },
  { category: 'BUG_FIX', label: 'Bug Fixes', emoji: '🐞' },
  { category: 'SECURITY_UPDATE', label: 'Security Updates', emoji: '🔒' },
  { category: 'BREAKING_CHANGE', label: 'Breaking Changes', emoji: '⚠️' },
];

export type ReleaseUpdateItemDto = {
  id: string;
  category: ReleaseUpdateCategory;
  title: string;
  description: string;
  displayOrder: number;
  imageUrls: string[];
};

export type ReleaseNoteStatus = 'DRAFT' | 'PUBLISHED';

export type ReleaseNoteDto = {
  _id: string;
  versionNumber: string;
  buildNumber: string;
  releaseDate: string;
  title: string;
  shortSummary: string;
  status: ReleaseNoteStatus;
  displayOrder: number;
  updateItems: ReleaseUpdateItemDto[];
  createdBy?: string;
  createdByUser?: { userId: string; displayName: string };
  publishedAt?: string;
  createdAt?: string;
  updatedAt?: string;
};

export type CreateReleaseNotePayload = {
  versionNumber: string;
  buildNumber: string;
  releaseDate: string;
  title: string;
  shortSummary: string;
  status?: ReleaseNoteStatus;
  displayOrder?: number;
  updateItems?: ReleaseUpdateItemDto[];
};

export type UpdateReleaseNotePayload = Partial<CreateReleaseNotePayload>;

export type ListReleaseNotesParams = {
  search?: string;
};

export type ReleaseUpdateItemForm = {
  id: string;
  category: ReleaseUpdateCategory;
  title: string;
  description: string;
  displayOrder: number;
  imageUrls: string[];
};

export type ReleaseNoteForm = {
  versionNumber: string;
  buildNumber: string;
  releaseDate: string;
  title: string;
  shortSummary: string;
  status: ReleaseNoteStatus;
  displayOrder: number;
  updateItems: ReleaseUpdateItemForm[];
};

export function emptyReleaseUpdateItem(displayOrder = 0): ReleaseUpdateItemForm {
  return {
    id: crypto.randomUUID(),
    category: 'NEW_FEATURE',
    title: '',
    description: '',
    displayOrder,
    imageUrls: [],
  };
}

export function emptyReleaseNoteForm(): ReleaseNoteForm {
  return {
    versionNumber: '',
    buildNumber: '',
    releaseDate: new Date().toISOString().slice(0, 10),
    title: '',
    shortSummary: '',
    status: 'DRAFT',
    displayOrder: 0,
    updateItems: [emptyReleaseUpdateItem()],
  };
}

export function releaseNoteToForm(note: ReleaseNoteDto): ReleaseNoteForm {
  return {
    versionNumber: note.versionNumber,
    buildNumber: note.buildNumber,
    releaseDate: note.releaseDate.slice(0, 10),
    title: note.title,
    shortSummary: note.shortSummary,
    status: note.status,
    displayOrder: note.displayOrder,
    updateItems: note.updateItems.map((item) => ({ ...item })),
  };
}

export function formatReleaseDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });
}

export function releaseMatchesSearch(note: ReleaseNoteDto, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    note.versionNumber,
    note.buildNumber,
    note.title,
    note.shortSummary,
    formatReleaseDate(note.releaseDate),
    note.releaseDate.slice(0, 10),
    ...note.updateItems.flatMap((item) => [item.title, item.description]),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}
